import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  withStyles,
} from '@material-ui/core';
import EditBarIcon from 'components/Icons/EditBarIcon';
import NoPaperPageContent from 'components/Generic/NoPaperPageContent';
import { useBuyerById } from 'hooks';
import Loading from 'components/Loading';
import { Routes } from 'routes';
import { colors, fonts } from 'theme';
import { Buyer, Contact, Event, UserRole } from 'types';
import client from 'services/client';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import BuyerMaterialEditForm from 'components/Forms/BuyerMaterialEditForm';
import { StreamCommand } from './types';

const useStyles = makeStyles({
  label: {
    fontWeight: 600,
    marginRight: 16,

    '&:after': {
      content: '" :"',
    },
  },
  cardLabel: {
    fontWeight: 600,
    marginRight: 16,
  },
  marginTopDivider: {
    marginTop: '3em',
  },
  buyerCard: {
    padding: '39px',
    minHeight: '300px',
  },
  materialsCard: {
    padding: '23px 0 48px 39px',
  },
  cardTitle: {
    justifyContent: 'space-between',
    fontSize: '24px',
    paddingLeft: '0.3em',
    marginBottom: '15px',
    paddingBottom: '5px',
    marginRight: '27px',
    color: colors.primary,
    borderBottom: `1px solid ${colors.greyLighten10}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '48px',
  },
  editAction: {
    '& > button': {
      color: colors.grey900,
      '& > span': {
        '& > svg': {
          fontSize: '1.28em',
        },
      },
    },
  },
  selectInTd: {
    width: '98%',
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '12',
    border: 'none',
    padding: '2px 0',
  },
})(TableCell);

interface Line {
  key: string;
  value: string | number;
}

interface BuyerCardProps {
  title: string;
  lines: Line[];
}

const BuyerCard: React.FC<BuyerCardProps> = ({ title, lines }: BuyerCardProps) => {
  const classes = useStyles();

  return (
    <Card className={classes.buyerCard}>
      <Typography variant="h2" className={classes.cardTitle}>
        {title}
      </Typography>
      <Table>
        <TableBody>
          {lines.map((line, index) => (
            <TableRow key={`${title}_table_${index}`}>
              <StyledTableCell width="50%">
                <span className={classes.cardLabel}>{line.key}</span>
              </StyledTableCell>
              <StyledTableCell width="50%">{`: ${line.value}`}</StyledTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const formatBuyerProperty = (buyer: Buyer | null | undefined, property: keyof Buyer) => {
  return buyer && buyer[property] ? `${buyer[property]}` : '-';
};

const fomatContactProperty = (buyer: Buyer | null | undefined, property: keyof Contact) => {
  return buyer && buyer.contact && buyer.contact[property] ? `${buyer.contact[property]}` : '-';
};

const BuyerShow: React.FC = () => {
  const classes = useStyles();
  const { buyerId } = useParams<{ buyerId: string }>();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'buyers' });
  const { value: buyer, loading } = useBuyerById(buyerId);
  const context = useContext(AppContext);
  const roles = context.getRoles?.();
  const isUserAbleToEdit = roles && (roles.includes(UserRole.ROLE_MANAGER) || context.hasLeastOneAdminRole());

  const clientCardLines = [
    {
      key: pt('name'),
      value: formatBuyerProperty(buyer, 'name'),
    },
    {
      key: pt('addressLine'),
      value: formatBuyerProperty(buyer, 'addressLine'),
    },
    {
      key: pt('postalCode'),
      value: formatBuyerProperty(buyer, 'postalCode'),
    },
    {
      key: pt('city'),
      value: formatBuyerProperty(buyer, 'city'),
    },
    {
      key: pt('phone'),
      value: formatBuyerProperty(buyer, 'phoneNumber'),
    },
  ];

  const contactCardList = [
    {
      key: t('contacts.lastName'),
      value: fomatContactProperty(buyer, 'lastName'),
    },
    {
      key: t('contacts.firstName'),
      value: fomatContactProperty(buyer, 'firstName'),
    },
    {
      key: t('contacts.email'),
      value: fomatContactProperty(buyer, 'email'),
    },
    {
      key: t('contacts.phone'),
      value: fomatContactProperty(buyer, 'phoneNumber'),
    },
  ];

  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = async (buyerMaterials: StreamCommand) => {
    if (buyerMaterials.composition.length > 0) {
      try {
        await client.put(`/buyers/${buyer?.id}`, { acceptedMaterials: buyerMaterials.composition });
        notify({
          message: pt('material.update.successMessage'),
          severity: 'success',
        });
        setIsEditing(false);
        PubSub.publish(Event.REFRESH_BUYERS);
      } catch {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      }
    }
  };

  return (
    <>
      <NoPaperPageContent title={t('buyers.show.title')} backRoute={Routes.Buyers}>
        {loading ? (
          <Loading />
        ) : (
          buyer && (
            <>
              <Divider className={classes.marginTopDivider} />
              <Box display="flex" paddingTop="26px" paddingBottom="26px">
                <Box marginRight="200px">
                  <Typography>
                    <span className={classes.label}>{pt('name')}</span>
                    {buyer.name}
                  </Typography>
                </Box>
                <Typography>
                  <span className={classes.label}>{pt('city')}</span>
                  {buyer.city}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="52px" marginRight="200px">
                <Typography>
                  <span className={classes.label}>{pt('postalCode')}</span>
                  {buyer.postalCode ? buyer.postalCode : '-'}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <BuyerCard title={pt('details')} lines={clientCardLines} />
                </Grid>
                <Grid item xs={4}>
                  <BuyerCard title={pt('contact')} lines={contactCardList} />
                </Grid>
                <Grid item xs={8}>
                  <Card className={classes.materialsCard}>
                    {!isEditing ? (
                      <>
                        <Typography variant="h2" className={classes.cardTitle}>
                          <div>{pt('materials')}</div>
                          <div className={classes.editAction}>
                            {isUserAbleToEdit && (
                              <IconButton disabled={isEditing} onClick={() => setIsEditing(true)}>
                                <EditBarIcon />
                              </IconButton>
                            )}
                          </div>
                        </Typography>
                        {buyer.acceptedMaterials.map(material => (
                          <Chip key={material['@id']} label={material.shortName} />
                        ))}
                      </>
                    ) : (
                      <BuyerMaterialEditForm
                        onCancel={() => {
                          setIsEditing(false);
                        }}
                        onSubmit={handleSubmit}
                        buyer={buyer}
                      />
                    )}
                  </Card>
                </Grid>
              </Grid>
            </>
          )
        )}
      </NoPaperPageContent>
    </>
  );
};
export default BuyerShow;
